const isApiRequest = (input) => {
  const requestUrl = typeof Request !== "undefined" && input instanceof Request
    ? input.url
    : input;

  try {
    const pathname = new URL(requestUrl, window.location.origin).pathname;
    return /\/api(?:\/|$)/i.test(pathname)
      && !/\/api\/web\/(?:login|sendOtp)(?:\/|$)/i.test(pathname);
  } catch {
    return false;
  }
};

const hasAuthorizationHeader = (input, options) => {
  const headers = options?.headers
    || (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined);

  return Boolean(headers && new Headers(headers).has("Authorization"));
};

const isSessionExpiredResponse = (body) => (
  body?.status === false
  && body.message === "Access denied."
  && body.data?.error === "A valid authenticated user token is required."
);

const installAuthResponseHandler = (onUnauthorized) => {
  const originalFetch = window.fetch;
  let expiryTimer;

  const handleUnauthorized = () => {
    clearTimeout(expiryTimer);
    onUnauthorized();
  };

  const scheduleTokenExpiry = () => {
    clearTimeout(expiryTimer);

    const token = sessionStorage.getItem("token");
    if (sessionStorage.getItem("authenticated") !== "1" || !token) {
      return;
    }

    try {
      const payload = token.split(".")[1];
      if (!payload) {
        throw new Error("Malformed authentication token");
      }

      const base64Payload = payload
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(payload.length / 4) * 4, "=");
      const expiry = Number(JSON.parse(atob(base64Payload)).exp) * 1000;
      if (!Number.isFinite(expiry)) {
        throw new Error("Authentication token has no valid expiration");
      }

      const timeUntilExpiry = expiry - Date.now();
      if (timeUntilExpiry <= 0) {
        handleUnauthorized();
        return;
      }

      expiryTimer = setTimeout(handleUnauthorized, timeUntilExpiry);
    } catch {
      handleUnauthorized();
    }
  };

  scheduleTokenExpiry();
  window.addEventListener("rankplus-auth-state-change", scheduleTokenExpiry);

  const authenticatedFetch = async (...args) => {
    const response = await originalFetch(...args);
    if (
      response.status === 401
      && sessionStorage.getItem("authenticated") === "1"
      && sessionStorage.getItem("token")
      && isApiRequest(args[0])
      && hasAuthorizationHeader(args[0], args[1])
    ) {
      try {
        const body = await response.clone().json();
        if (isSessionExpiredResponse(body)) {
          handleUnauthorized();
        }
      } catch {
        // A non-JSON or unreadable 401 response is not a session-expiration response.
      }
    }
    return response;
  };

  window.fetch = authenticatedFetch;

  return () => {
    clearTimeout(expiryTimer);
    window.removeEventListener("rankplus-auth-state-change", scheduleTokenExpiry);
    if (window.fetch === authenticatedFetch) {
      window.fetch = originalFetch;
    }
  };
};

export default installAuthResponseHandler;
